import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Button, Divider, Empty, Flex, Grid, Result, Typography } from 'antd';
import { Download, Plus } from 'lucide-react';

import { ImportHistoryTable, useListImportJobsInfiniteQuery } from '../../../../modules/import';
import { TextTertiary } from '../../../../ui/components/text';
import { useLayoutInsets } from '../../../../ui/providers/layout-insets';

const ImportHistoryRoute = () => {
  const navigate = useNavigate();
  const insets = useLayoutInsets();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListImportJobsInfiniteQuery(selectedOrganization.id);

  const jobs = data?.pages.flatMap((page) => page.data.items) ?? [];
  const total = data?.pages[0]?.data.total ?? 0;
  const recordsAdded = jobs.reduce(
    (sum, { output }) =>
      sum +
      (output.stats?.peopleCreated ?? 0) +
      (output.stats?.companiesCreated ?? 0) +
      (output.stats?.dealsCreated ?? 0),
    0,
  );

  const startImport = () => navigate({ to: '/import/new' });

  return (
    <Flex
      vertical
      gap={12}
      style={{
        height: `calc(100dvh - ${insets.top}px)`,
        padding: isMobile ? '12px 12px 16px' : '16px 24px 24px',
      }}
    >
      <Flex align="center" gap={8} wrap>
        <Flex
          align="center"
          justify="center"
          style={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 10,
            background: 'var(--ant-color-bg-container)',
            border: '1px solid var(--ant-color-border-secondary)',
            color: 'var(--ant-color-text-secondary)',
          }}
        >
          <Download size={17} />
        </Flex>
        <Flex vertical style={{ lineHeight: 1.2 }}>
          <Typography.Text style={{ fontSize: 15, fontWeight: 600 }}>Imports</Typography.Text>
          <TextTertiary style={{ fontSize: 12 }}>
            {total} {total === 1 ? 'import' : 'imports'} · {recordsAdded.toLocaleString()} records
            added
          </TextTertiary>
        </Flex>
        <Flex flex={1} />
        <Divider orientation="vertical" style={{ height: 22, margin: 0 }} />
        <Button
          data-testid="import-new"
          type="primary"
          icon={<Plus size={13} />}
          style={{ height: 34, paddingInline: 15, fontSize: 13, fontWeight: 600 }}
          onClick={startImport}
        >
          New import
        </Button>
      </Flex>

      {isError ? (
        <Result
          status="error"
          title="Failed to load import history"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button type="primary" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ) : (
        <ImportHistoryTable
          jobs={jobs}
          isLoading={isPending}
          isLoadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          emptyText={
            <Flex vertical align="center" gap={16} style={{ padding: '40px 0' }}>
              <Empty description="No imports yet" />
              <Button type="primary" icon={<Plus size={13} />} onClick={startImport}>
                New import
              </Button>
            </Flex>
          }
          onSelectJob={(importJobId) =>
            navigate({ to: '/import/$importJobId', params: { importJobId } })
          }
          onLoadMore={fetchNextPage}
        />
      )}
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/import/')({
  component: ImportHistoryRoute,
});
